import { useEffect, useId, useRef, useState, type PropsWithChildren } from "react";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { ConvexReactClient, useConvexAuth, useMutation } from "convex/react";
import {
  createDesktopWorkspaceAuthClient,
  createDesktopWorkspaceAuthStorage,
  createSameOriginWorkspaceAuthClient,
  type WorkspaceAuthClient,
} from "@papersflow/auth/client";
import { desktopBridge } from "@papersflow/desktop-bridge";
import { api } from "../../../backend/convex/_generated/api";
import { WorkspaceLoadingShell } from "../components/WorkspaceLoadingShell";
import { VoiceSessionApiProvider } from "../components/voice/voiceSessionApi";
import {
  createWorkspaceAccessBootstrap,
  type WorkspaceAccessBootstrap,
} from "./workspaceAccessBootstrap";

const convexUrl = import.meta.env.VITE_CONVEX_URL?.trim();
const convexSiteUrl = import.meta.env.VITE_CONVEX_SITE_URL?.trim();
const backendConfig =
  convexUrl && convexSiteUrl
    ? {
        convexUrl,
        convexSiteUrl,
      }
    : undefined;

const convex = backendConfig
  ? new ConvexReactClient(backendConfig.convexUrl, {
      expectAuth: true,
      initialAuthTokenReuse: true,
    })
  : undefined;

interface WorkspaceAuthInitialization {
  readonly client: WorkspaceAuthClient;
  readonly canCreateDeviceIdentity: boolean;
  readonly access: WorkspaceAccessBootstrap;
}

async function initializeWorkspaceAuthClient(): Promise<WorkspaceAuthInitialization> {
  if (!backendConfig) throw new Error("Missing PapersFlow backend configuration.");
  if (!desktopBridge.isDesktop) {
    const client = createSameOriginWorkspaceAuthClient(window.location.origin);
    return {
      client,
      canCreateDeviceIdentity: true,
      access: createWorkspaceAccessBootstrap(client),
    };
  }

  const session = await desktopBridge.authSession.load();
  const storage = createDesktopWorkspaceAuthStorage(session.cookie, (cookie) =>
    desktopBridge.authSession.store(cookie),
  );
  const client = createDesktopWorkspaceAuthClient(backendConfig.convexSiteUrl, storage);
  return {
    client,
    canCreateDeviceIdentity: session.cookie === null,
    access: createWorkspaceAccessBootstrap(client),
  };
}

let workspaceAuthClientPromise: Promise<WorkspaceAuthInitialization> | undefined;

type WorkspaceAuthClientState =
  | { readonly status: "uninitialized" }
  | { readonly status: "ready"; readonly initialization: WorkspaceAuthInitialization };

let workspaceAuthClientState: WorkspaceAuthClientState = { status: "uninitialized" };

function getReadyWorkspaceAuthInitialization(): WorkspaceAuthInitialization | null {
  return workspaceAuthClientState.status === "ready"
    ? workspaceAuthClientState.initialization
    : null;
}

export function getReadyWorkspaceAuthClient(): WorkspaceAuthClient | null {
  return getReadyWorkspaceAuthInitialization()?.client ?? null;
}

function getWorkspaceAuthClient(): Promise<WorkspaceAuthInitialization> {
  workspaceAuthClientPromise ??= initializeWorkspaceAuthClient().then((initialization) => {
    workspaceAuthClientState = { status: "ready", initialization };
    return initialization;
  });
  return workspaceAuthClientPromise;
}

const authenticationRecoveryDelayMs = 10_000;

export async function getWorkspaceBearerToken(): Promise<string | null> {
  const client = getReadyWorkspaceAuthClient();
  if (!client) return null;
  try {
    const { data } = await client.convex.token({ fetchOptions: { throw: false } });
    return data?.token ?? null;
  } catch {
    return null;
  }
}

export function BackendProviders({ children }: PropsWithChildren) {
  if (!backendConfig || !convex) return <MissingConvexConfiguration />;
  return (
    <InitializedBackendProviders convexClient={convex}>{children}</InitializedBackendProviders>
  );
}

type AuthClientInitializationState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly initialization: WorkspaceAuthInitialization }
  | { readonly status: "failed" };

function InitializedBackendProviders({
  convexClient,
  children,
}: PropsWithChildren<{ readonly convexClient: ConvexReactClient }>) {
  const [state, setState] = useState<AuthClientInitializationState>(() => {
    const initialization = getReadyWorkspaceAuthInitialization();
    return initialization ? { status: "ready", initialization } : { status: "loading" };
  });

  useEffect(() => {
    if (state.status !== "loading") return undefined;
    let active = true;
    void getWorkspaceAuthClient().then(
      (initialization) => {
        if (active) setState({ status: "ready", initialization });
        return undefined;
      },
      () => {
        if (active) setState({ status: "failed" });
        return undefined;
      },
    );
    return () => {
      active = false;
    };
  }, [state.status]);

  if (state.status === "loading") return <AuthenticationLoadingShell />;
  if (state.status === "failed") {
    return (
      <BackendStatus
        title="Secure session unavailable"
        detail={
          desktopBridge.isDesktop
            ? "Unlock the operating-system credential store, then reload PapersFlow."
            : "Reload PapersFlow to restore the secure web session."
        }
      >
        <button
          type="button"
          className="mt-4 inline-flex h-8 items-center rounded-md border border-[var(--border)] px-3 text-[10px] font-semibold text-[var(--text)] hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
          onClick={() => window.location.reload()}
        >
          Reload PapersFlow
        </button>
      </BackendStatus>
    );
  }

  return (
    <ConvexBetterAuthProvider client={convexClient} authClient={state.initialization.client}>
      <AuthenticationGate initialization={state.initialization}>{children}</AuthenticationGate>
    </ConvexBetterAuthProvider>
  );
}

function AuthenticationGate({
  initialization,
  children,
}: PropsWithChildren<{ readonly initialization: WorkspaceAuthInitialization }>) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const [automaticAccessFailed, setAutomaticAccessFailed] = useState(false);

  useEffect(() => {
    if (
      isLoading ||
      isAuthenticated ||
      !initialization.canCreateDeviceIdentity ||
      automaticAccessFailed
    ) {
      return undefined;
    }
    let active = true;
    void initialization.access.start().catch(() => {
      if (active) setAutomaticAccessFailed(true);
    });
    return () => {
      active = false;
    };
  }, [automaticAccessFailed, initialization, isAuthenticated, isLoading]);

  if (isAuthenticated) return <PersonalWorkspaceGate>{children}</PersonalWorkspaceGate>;
  if (isLoading || (initialization.canCreateDeviceIdentity && !automaticAccessFailed)) {
    return <AuthenticationLoadingShell />;
  }
  if (!initialization.canCreateDeviceIdentity) {
    return <SavedSessionRecovery />;
  }
  return <WorkspaceLoadingShell onReload={() => window.location.reload()} />;
}

function SavedSessionRecovery() {
  const [state, setState] = useState<"idle" | "confirming" | "clearing">("idle");
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmationTitleId = useId();
  const confirmationDetailId = useId();

  useEffect(() => {
    if (state === "confirming") cancelRef.current?.focus({ preventScroll: true });
  }, [state]);

  const clearSavedSession = async () => {
    if (state !== "confirming") return;
    setState("clearing");
    setError(null);
    try {
      await desktopBridge.authSession.store(null);
      window.location.reload();
    } catch {
      setState("confirming");
      setError(
        "PapersFlow could not clear the saved session. Unlock the credential store and retry.",
      );
    }
  };

  return (
    <BackendStatus
      title="Saved session unavailable"
      detail="PapersFlow will not replace this device identity automatically. Restore the saved session and reload, or clear it explicitly."
    >
      {state === "idle" ? (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            className="inline-flex h-8 items-center rounded-md border border-[var(--border)] px-3 text-[10px] font-semibold text-[var(--text)] hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
            onClick={() => window.location.reload()}
          >
            Reload PapersFlow
          </button>
          <button
            type="button"
            className="inline-flex h-8 items-center rounded-md border border-[var(--destructive-border)] px-3 text-[10px] font-semibold text-[var(--destructive-text)] hover:bg-[var(--destructive-subtle)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
            onClick={() => setState("confirming")}
          >
            Clear saved session
          </button>
        </div>
      ) : (
        <div
          role="alertdialog"
          aria-labelledby={confirmationTitleId}
          aria-describedby={confirmationDetailId}
          className="mt-4 rounded-md border border-[var(--destructive-border)] bg-[var(--destructive-subtle)] p-3 text-left"
        >
          <p id={confirmationTitleId} className="text-[10px] font-semibold text-[var(--text)]">
            Clear this Mac&apos;s saved session?
          </p>
          <p
            id={confirmationDetailId}
            className="mt-1 max-w-80 text-[10px] leading-4 text-[var(--muted-2)]"
          >
            This removes the device credential and may make its current private workspace
            inaccessible. Project files on disk are not deleted.
          </p>
          {error ? (
            <p role="alert" className="mt-2 max-w-80 text-[10px] text-[var(--destructive-text)]">
              {error}
            </p>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              disabled={state === "clearing"}
              className="h-7 rounded-md border border-[var(--border)] px-2.5 text-[10px] font-semibold text-[var(--text)] hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)] disabled:opacity-50"
              onClick={() => {
                setError(null);
                setState("idle");
              }}
            >
              Keep saved session
            </button>
            <button
              type="button"
              disabled={state === "clearing"}
              className="h-7 rounded-md bg-[var(--destructive-text)] px-2.5 text-[10px] font-semibold text-[var(--destructive-foreground)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)] disabled:opacity-50"
              onClick={() => void clearSavedSession()}
            >
              {state === "clearing" ? "Clearing…" : "Clear and restart"}
            </button>
          </div>
        </div>
      )}
    </BackendStatus>
  );
}

function PersonalWorkspaceGate({ children }: PropsWithChildren) {
  const ensurePersonal = useMutation(api.workspaces.ensurePersonal);
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");

  useEffect(() => {
    if (status !== "loading") return undefined;
    let active = true;
    void ensurePersonal({ name: "Personal workspace" }).then(
      () => {
        if (active) setStatus("ready");
        return undefined;
      },
      () => {
        if (active) setStatus("failed");
        return undefined;
      },
    );
    return () => {
      active = false;
    };
  }, [ensurePersonal, status]);

  if (status === "loading") return <AuthenticationLoadingShell />;
  if (status === "failed") {
    return (
      <BackendStatus
        title="Workspace unavailable"
        detail="Reload PapersFlow to retry opening this device workspace."
      />
    );
  }
  return <VoiceSessionApiProvider>{children}</VoiceSessionApiProvider>;
}

function AuthenticationLoadingShell() {
  const [canReload, setCanReload] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setCanReload(true), authenticationRecoveryDelayMs);
    return () => window.clearTimeout(timeout);
  }, []);

  if (canReload) {
    return <WorkspaceLoadingShell onReload={() => window.location.reload()} />;
  }
  return <WorkspaceLoadingShell />;
}

function MissingConvexConfiguration() {
  return (
    <BackendStatus
      title="Convex is not configured"
      detail="Set VITE_CONVEX_URL and VITE_CONVEX_SITE_URL, then run pnpm dev:convex."
    />
  );
}

function BackendStatus({
  title,
  detail,
  children,
}: PropsWithChildren<{ readonly title: string; readonly detail?: string }>) {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--background)] px-6 text-center text-[var(--text)]">
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {detail ? <p className="mt-2 text-xs text-[var(--muted-2)]">{detail}</p> : null}
        {children}
      </div>
    </main>
  );
}
